import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Loader2, Check, X, Clock, CheckCircle2, XCircle, User } from 'lucide-react';

// Review panel shown to the promoter on the book detail page.
// Lists pending (and reviewed) co-promoter suggestions and lets the promoter
// approve or reject each one. Approved ones are merged into the client form.

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'object') {
    try {
      return Object.entries(val).filter(([k]) => !k.startsWith('_')).map(([k, v]) =>
        v ? `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}` : null
      ).filter(Boolean).join(' · ');
    } catch {
      return JSON.stringify(val);
    }
  }
  return String(val);
};

const statusBadge = (status) => {
  if (status === 'approved') {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><CheckCircle2 className="w-3 h-3" /> Aceptada</Badge>;
  }
  if (status === 'rejected') {
    return <Badge className="bg-red-100 text-red-700 border-red-200 gap-1"><XCircle className="w-3 h-3" /> Descartada</Badge>;
  }
  return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" /> Pendiente</Badge>;
};

export const CoPromoterSuggestionsPanel = ({ bookId, onChange, onAllResolved }) => {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showAll, setShowAll] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/books/${bookId}/co-promoter-suggestions`,
        { withCredentials: true },
      );
      setSuggestions(res.data.suggestions || []);
      setPendingCount(res.data.pending_count || 0);
    } catch {
      // silent — the panel is optional
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  // Common post-decision routine: refresh + notify + smart-return to the
  // Panel de control when the LAST pending suggestion has been resolved.
  const postDecisionRefresh = async () => {
    let remaining = 0;
    try {
      const res = await axios.get(
        `${API_URL}/api/books/${bookId}/co-promoter-suggestions`,
        { withCredentials: true },
      );
      setSuggestions(res.data.suggestions || []);
      setPendingCount(res.data.pending_count || 0);
      remaining = res.data.pending_count || 0;
    } catch {
      // fall back to silent refresh
    }
    onChange?.();
    if (remaining === 0) {
      onAllResolved?.();
    }
  };

  const approve = async (sid) => {
    setBusyId(sid);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/co-promoter-suggestions/${sid}/approve`,
        {},
        { withCredentials: true },
      );
      toast.success('Aportación incorporada al formulario');
      await postDecisionRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aprobar');
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/co-promoter-suggestions/${rejectTarget}/reject`,
        { reason: rejectReason.trim() },
        { withCredentials: true },
      );
      toast.message('Aportación descartada');
      setRejectTarget(null);
      setRejectReason('');
      await postDecisionRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descartar');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-4"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (suggestions.length === 0) {
    return null;
  }

  const visible = showAll ? suggestions : suggestions.filter(s => s.status === 'pending');

  return (
    <Card className="card-elevated" data-testid="co-promoter-suggestions-panel">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              Aportaciones de copatrocinadores
              {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200">{pendingCount} pendiente{pendingCount === 1 ? '' : 's'}</Badge>}
            </CardTitle>
            <CardDescription className="text-xs">
              Revisa lo que tus copatrocinadores quieren añadir al contenido del protagonista. Lo aceptado pasa al formulario del protagonista; lo rechazado se archiva.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setShowAll(v => !v)}
            data-testid="toggle-all-suggestions"
          >
            {showAll ? 'Ver sólo pendientes' : `Ver historial (${suggestions.length})`}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {visible.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">Sin aportaciones pendientes.</p>
        ) : visible.map(s => (
          <div key={s.suggestion_id} className="rounded-md border p-3 space-y-2 bg-muted/20" data-testid={`suggestion-${s.suggestion_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                  <User className="w-3.5 h-3.5" />
                  <span className="font-medium text-foreground">{s.co_promoter_name}</span>
                  <span className="text-muted-foreground/60">·</span>
                  <span>{s.operation === 'add_list_item' ? 'Nuevo elemento' : 'Añadir texto'}</span>
                </div>
                <Badge variant="outline" className="text-[11px] font-medium bg-white">
                  <span className="text-foreground">{s.section_title || s.section_id}</span>
                  {s.field_label && (
                    <span className="text-muted-foreground"> · {s.field_label}</span>
                  )}
                </Badge>
                {/* Show the promoter's current value for context when present
                    so the reviewer sees the original text alongside the new
                    proposal. Only relevant for add_text suggestions. */}
                {s.operation === 'add_text' && s.current_value_preview && (
                  <div className="mt-2 rounded-md border border-dashed bg-white p-2.5 text-xs space-y-0.5">
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">Contenido actual del promotor</p>
                    <p className="text-foreground/80 whitespace-pre-wrap line-clamp-4">{s.current_value_preview}</p>
                  </div>
                )}
                <div className="rounded-md border-l-4 border-l-amber-500 bg-amber-50/60 p-2.5 text-sm space-y-0.5">
                  <p className="text-[10px] uppercase tracking-wide font-semibold text-amber-900">Aportación propuesta</p>
                  <p className="text-foreground whitespace-pre-wrap">
                    {typeof s.value === 'string' ? s.value : formatValue(s.value)}
                  </p>
                </div>
                {s.note && (
                  <p className="text-xs italic text-muted-foreground">Nota del copatrocinador: {s.note}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {statusBadge(s.status)}
                {/* Toggle actions: allow the promoter to activate / deactivate
                    an aportación at any time, regardless of its current
                    status, so they can rectify an earlier decision. */}
                {(s.status === 'pending' || s.status === 'rejected') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => approve(s.suggestion_id)}
                    disabled={busyId === s.suggestion_id}
                    className="gap-1 h-8"
                    title={s.status === 'rejected' ? 'Volver a incluir esta aportación en el libro' : 'Aceptar e incluir'}
                    data-testid={`approve-${s.suggestion_id}`}
                  >
                    {busyId === s.suggestion_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-emerald-600" />}
                    {s.status === 'rejected' ? 'Reactivar' : 'Aceptar'}
                  </Button>
                )}
                {(s.status === 'pending' || s.status === 'approved') && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRejectTarget(s.suggestion_id)}
                    disabled={busyId === s.suggestion_id}
                    className="gap-1 h-8 text-red-600"
                    title={s.status === 'approved' ? 'Quitar del libro (podrás reactivarla)' : 'Descartar'}
                    data-testid={`reject-${s.suggestion_id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                    {s.status === 'approved' ? 'Desactivar' : 'Descartar'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Descartar aportación</DialogTitle>
            <DialogDescription>
              Si quieres, añade una nota para recordar por qué la has descartado (no se envía al copatrocinador).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Motivo (opcional)"
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={!!busyId} className="gap-2">
              {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CoPromoterSuggestionsPanel;
